import type React from "react";
import { useEffect, useState } from "react";
import "./MatchWordsActivity.scss";
import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import { ButtonToolbar, IconButton } from "rsuite";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { shuffle } from "../../../shared/helpers";
import { Definition } from "./components/Definition";
import { Word } from "./components/Word";
import type { WordsAndDefinitionsType } from "./type";

type Props = {
	data: WordsAndDefinitionsType[];
	matchWithAudio?: boolean;
	activityId: string;
};

type ExtensionType = {
	answer: string;
	index: number | null;
	correct?: null | boolean | undefined;
};

type ExtendedWordsAndDefinitionsType = WordsAndDefinitionsType & ExtensionType;

const MatchWordsActivity: React.FC<Props> = ({
	data,
	matchWithAudio,
	activityId,
}) => {
	const [words, setWords] = useState<string[]>([]);
	const [extendedData, setExtendedData] = useState<
		ExtendedWordsAndDefinitionsType[]
	>([]);
	const { triggerActivityCompletion } = useActivityCompletion();

	const initialize = () => {
		const words = data.map((item) => item.word);
		const extended = data.map((item) => ({ ...item, answer: "", index: null }));

		const shuffledWords = shuffle(words);

		setWords(shuffledWords);
		setExtendedData(extended);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		initialize();
	}, []);

	const handleDropWord = (definition: string, word: string) => {
		const newWords = [...words];
		const index = newWords.findIndex((item) => item === word);
		newWords[index] = "";

		setWords(newWords);

		const newExtendedData = [...extendedData];
		const foundExtendedData = newExtendedData.find(
			(item) => item.definition === definition,
		) as {
			word: string;
			definition: string;
			answer: string;
			index: number;
		};

		foundExtendedData.answer = word;
		foundExtendedData.index = index;

		setExtendedData(newExtendedData);
	};

	const handleRemoveWord = (definition: string, word: string) => {
		const newExtendedData = [...extendedData];
		const foundExtendedData = newExtendedData.find(
			(item) => item.definition === definition,
		);
		let wordIndex = null;
		if (foundExtendedData) {
			wordIndex = foundExtendedData.index;
			foundExtendedData.answer = "";
			foundExtendedData.index = null;
		}
		setExtendedData(newExtendedData);

		const newWords = [...words];
		if (wordIndex !== null) {
			newWords[wordIndex] = word;
		} else {
			newWords.push(word);
		}
		setWords(newWords);
	};

	const resetHandler = () => {
		initialize();
	};

	const checkHandler = async () => {
		const updatedExtendedData = extendedData.map((item) => {
			return {
				...item,
				correct: item.answer === item.word,
			};
		});

		const allItemsHasAnswers = updatedExtendedData.every(
			(item) => item.answer !== "",
		);
		const correctAnswers = updatedExtendedData.filter(
			(item) => item.correct === true,
		).length;

		if (!allItemsHasAnswers) {
			return;
		}
		setExtendedData(updatedExtendedData);
		await triggerActivityCompletion(activityId, correctAnswers, resetHandler);
	};

	return (
		<div className="match-words__container">
			<ButtonToolbar>
				<IconButton
					onClick={resetHandler}
					appearance="primary"
					icon={<ReloadIcon color="white" />}
				/>
				<IconButton
					onClick={checkHandler}
					appearance="primary"
					icon={<CheckOutlineIcon color="white" />}
				>
					Check
				</IconButton>
			</ButtonToolbar>
			<div className="match-words__holder">
				<ul className="match-words__definitions-list">
					{extendedData?.map((item, index) => (
						<Definition
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							key={index}
							matchWithAudio={matchWithAudio}
							onRemoveWord={handleRemoveWord}
							definition={item.definition}
							droppedWord={item.answer}
							onDropWord={handleDropWord}
							correct={item.correct}
						/>
					))}
				</ul>
				<ul className="match-words__words-list">
					{words?.map((item, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						<Word matchWithAudio={matchWithAudio} key={index} word={item} />
					))}
				</ul>
			</div>
		</div>
	);
};

export default MatchWordsActivity;
